import { useState } from "react";
import { ChevronRight, ChevronUp, Package } from "lucide-react";
import { Link } from "wouter";
import { useI18n } from "../lib/i18n";

const VISIBLE_SHORTCUT_COUNT = 4;
const SHORTCUTS_PER_ROW = 4;

// Supplied account-menu artwork is shared by the English and Tagalog labels.
const MENU_SHORTCUTS = {
  "My Items": { labelKey: "dashboard.accountMenu.myItems", Icon: Package },
  "My Services": { labelKey: "dashboard.accountMenu.myServices", iconSrc: "/assets/account-menu/services.svg" },
  "My Company": { labelKey: "dashboard.accountMenu.myCompany", iconSrc: "/assets/account-menu/company.svg" },
  "My Jobs": { labelKey: "dashboard.accountMenu.myJobs", iconSrc: "/assets/account-menu/jobs.svg" },
  "Hiring": { labelKey: "dashboard.accountMenu.hiring", iconSrc: "/assets/account-menu/hiring.svg" },
  "My Projects": { labelKey: "dashboard.accountMenu.myProjects", iconSrc: "/assets/account-menu/projects.svg" },
  "My Bids": { labelKey: "dashboard.accountMenu.myBids", iconSrc: "/assets/account-menu/bids.svg" },
  "My Applications": { labelKey: "dashboard.accountMenu.myApplications", iconSrc: "/assets/account-menu/applications.svg" },
  "Messages": { labelKey: "dashboard.accountMenu.messages", iconSrc: "/assets/account-menu/messages.svg" },
  "Contracts": { labelKey: "dashboard.accountMenu.contracts", iconSrc: "/assets/account-menu/contracts.svg" },
  "Wallet / Escrow": { labelKey: "dashboard.accountMenu.wallet", iconSrc: "/assets/account-menu/wallet.svg" },
  "Saved Items": { labelKey: "dashboard.accountMenu.saved", iconSrc: "/assets/account-menu/saved.svg" },
  "Settings": { labelKey: "dashboard.accountMenu.settings", iconSrc: "/assets/account-menu/settings.svg" },
} as const;

export type AccountMenuLink = {
  label: keyof typeof MENU_SHORTCUTS;
  fullLabel: string;
  href: string;
};

type MobileAccountMenuProps = {
  links: readonly AccountMenuLink[];
};

/** Account overview shortcuts; the caller supplies permission-filtered destinations. */
export function MobileAccountMenu({ links }: MobileAccountMenuProps) {
  const { t } = useI18n();
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleLinks = isExpanded ? links : links.slice(0, VISIBLE_SHORTCUT_COUNT);
  const rows: AccountMenuLink[][] = [];
  for (let index = 0; index < visibleLinks.length; index += SHORTCUTS_PER_ROW) {
    rows.push(visibleLinks.slice(index, index + SHORTCUTS_PER_ROW));
  }

  return (
    <section className="mobile-account-menu sm:hidden" aria-labelledby="account-menu-title">
      <div className="mobile-account-menu-heading">
        <h2 id="account-menu-title">{t("dashboard.accountMenu.title")}</h2>
        {links.length > VISIBLE_SHORTCUT_COUNT && (
          <button type="button" aria-expanded={isExpanded} aria-controls="mobile-account-shortcuts"
            onClick={() => setIsExpanded(!isExpanded)}>
            {t(isExpanded ? "dashboard.accountMenu.less" : "dashboard.accountMenu.more")}
            {isExpanded ? <ChevronUp aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
          </button>
        )}
      </div>
      <nav id="mobile-account-shortcuts" aria-label={t("dashboard.accountMenu.title")}>
        {rows.map((row) => (
          <div className="mobile-account-menu-row" key={row[0].label}>
            {row.map((link) => {
              const shortcut = MENU_SHORTCUTS[link.label];
              return (
                <Link key={link.label} href={link.href} aria-label={link.fullLabel} className="mobile-account-shortcut">
                  <span className="mobile-account-shortcut-icon">
                    {"Icon" in shortcut ? <shortcut.Icon aria-hidden="true" size={24} /> : <img src={shortcut.iconSrc} alt="" aria-hidden="true" width={24} height={24} />}
                  </span>
                  <span className="mobile-account-shortcut-label">{t(shortcut.labelKey)}</span>
                </Link>
              );
            })}
          </div>
        ))}
      </nav>
    </section>
  );
}
